import {
  AfterContentInit,
  Component,
  computed,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { AccountInforData } from 'src/app/shared/interfaces/account.interface';
import { ResourceApi } from '../../apis/resource.api';
import { DataCollectionService } from '../../service/data-collection.service';
import { LayoutService } from '../../service/layout.service';

type baseData<K extends string | number | symbol, T> = { [P in K]?: T };
type carouselType = 'FrontPage' | 'GamesPage' | 'LotteryPage' | 'SportPage' | 'SubSportPage' | 'LiveCasinoPage';
type carouselDatas = { img: string; href: string; id: number; customizedHref: string; gameProvider: string };

@UntilDestroy()
@Component({
  selector: 'app-normal-carousel',
  templateUrl: './normal-carousel.component.html',
  styleUrls: ['./normal-carousel.component.scss'],
  host: {
    '[class.hide-when-no-data]': '!loading && carouselData().length < 1',
  },
})
export class NormalCarouselComponent implements OnInit, AfterContentInit {
  constructor(
    private layout: LayoutService,
    private router: Router,
    private appService: AppService,
    private elementRef: ElementRef,
    private dataCollectionService: DataCollectionService,
    private resourceApi: ResourceApi
  ) {}

  static allCarouselData: baseData<carouselType, carouselDatas[]> = {};
  grid: WritableSignal<string> = signal('100%');

  carouselData: WritableSignal<carouselDatas[]> = signal([]);
  carouselDataChange$ = toObservable(this.carouselData).pipe(takeUntilDestroyed());
  renderCarouselData = computed(() => {
    if (this.carouselData().length > 0) return this.carouselData();
    return Array(3).fill(null);
  });

  @ViewChild('carousel') carousel?: ElementRef<HTMLElement>;

  @Input() buildFor?: carouselType;

  /** web 顶部banner为4个 H5为2个 */
  @Input() isSpecilStyleBanner: boolean = false;
  @Input() isSubscribeLayoutGrid: boolean = true;

  loading: boolean = true;

  isH5!: boolean;
  arrowLeft: boolean = false;
  arrowRight: boolean = false;
  active: boolean = false;

  @HostListener('mouseenter')
  onMouseenter() {
    this.active = true;
    this.checkArrow();
  }

  @HostListener('mouseleave')
  onMouseleave() {
    this.active = false;
  }

  checkArrowTimeout?: number;

  ngOnInit() {
    this.carouselDataChange$.subscribe(() => {
      if (this.carouselData().length > 0) {
        setTimeout(() => {
          this.checkArrow();
        });
      }
    });
    this.layout.isH5$.pipe(untilDestroyed(this)).subscribe(e => (this.isH5 = e));
    if (!this.buildFor) return;
    const data = NormalCarouselComponent.allCarouselData[this.buildFor] || [];
    // 如果是體育頁面強制重新打api對banner資料做排序，因為會有當前頁面登入登出排序問題
    if (this.buildFor === 'SportPage') {
      this.loading = true;

      combineLatest([
        this.appService.userInfo$, // 登入或沒登入都會跑
        this.resourceApi.getbannerlist(this.buildFor), // 拿 banner list
      ]).subscribe(([userInfo, bannerRes]) => {
        if (bannerRes?.data && bannerRes.data.length > 0) {
          const formatData = bannerRes.data.map((x, i) => {
            return {
              id: x.id,
              img: x.bannerUrl,
              href: x.imageUrl,
              customizedHref: x.imageUrl?.startsWith('http')
                ? x.imageUrl
                : `${window.location.origin}/${this.appService.languageCode}${x.imageUrl}`,
              gameProvider: x.gameProvider,
            };
          });
          NormalCarouselComponent.allCarouselData[this.buildFor as carouselType] = this.processBannerList(
            userInfo,
            formatData
          );

          this.carouselData.set(formatData);
        }
        this.loading = false;
      });
    } else {
      // 其他頁面跑原有流程不動
      if (data.length > 0) {
        this.carouselData.set(data);
        this.loading = false;
      } else {
        this.loading = true;
        this.resourceApi.getbannerlist(this.buildFor).subscribe(res => {
          if (res?.data && res.data.length > 0) {
            NormalCarouselComponent.allCarouselData[this.buildFor as carouselType] = res.data.map(x => ({
              id: x.id,
              img: x.bannerUrl,
              href: x.imageUrl,
              customizedHref: x.imageUrl?.startsWith('http')
                ? x.imageUrl
                : `${window.location.origin}/${this.appService.languageCode}${x.imageUrl}`,
              gameProvider: x.gameProvider,
            }));
            this.carouselData.set(
              NormalCarouselComponent.allCarouselData[this.buildFor as carouselType] as carouselDatas[]
            );
          }
          this.loading = false;
        });
      }
    }
  }

  sortCarouselDataByKey(data: carouselDatas[], key: string) {
    data.sort((a, b) => {
      if (a.gameProvider === key) {
        return -1;
      }
      if (b.gameProvider === key) {
        return 1;
      }
      return 0;
    });
  }

  /**
   * 體育頁面需要判斷有無登入以及沙巴品牌要放最前面
   * @param data
   * @returns
   */
  processBannerList(userInfo: AccountInforData | null, data: carouselDatas[]): carouselDatas[] {
    if (this.buildFor === 'SportPage') {
      // 根據後台設定的key來做排序，目前預設會是沙巴
      const { bannerSportSortKey } = this.appService.tenantConfig.config;

      if (bannerSportSortKey) {
        this.sortCarouselDataByKey(data, bannerSportSortKey as string);
      }

      if (userInfo?.lastSportsGameProvider) {
        this.sortCarouselDataByKey(data, userInfo.lastSportsGameProvider);
      }
    }

    return data;
  }

  onScroll(e: Event) {
    this.checkArrow(e.currentTarget as HTMLElement);
  }

  checkArrow(el: HTMLElement | undefined = this.carousel?.nativeElement) {
    if (!el) return;
    const sw = el.scrollWidth;
    const sl = el.scrollLeft;
    const w = el.offsetWidth;
    if (sw <= w) {
      this.arrowLeft = this.arrowRight = false;
      return;
    }
    this.arrowRight = sl + w + 1 < sw;
    this.arrowLeft = sl > 0;
  }

  clickItem(item: { img: string; href: string; id: number; customizedHref: string; gameProvider: string }) {
    if (!item) return;
    this.afterClickItem(item);
    if (item.href) {
      if ((item.href as string).startsWith('http')) {
        // 站外
        window.open(item.href);
      } else {
        // 路由
        this.router.navigateByUrl(this.appService.languageCode + item.href);
      }
    }
  }

  clickArrow(v: number) {
    if (this.carousel) {
      this.carousel.nativeElement.scrollLeft += this.carousel.nativeElement.offsetWidth * v;
    }
  }

  ngAfterContentInit() {
    // <=1000 2个
    // <=600 1个
    if (!this.isSubscribeLayoutGrid) {
      return;
    }
    this.layout
      .resizeObservable(this.elementRef.nativeElement)
      .pipe(
        map(e => e.contentRect.width),
        startWith(this.elementRef.nativeElement.offsetWidth),
        distinctUntilChanged(),
        untilDestroyed(this),
        filter(x => x > 0)
      )
      .subscribe(w => {
        if (!this.isSpecilStyleBanner) {
          if (w <= 600) {
            this.grid.set('100%');
          } else if (w <= 1000) {
            this.grid.set('50%');
          } else {
            this.grid.set('33.3333%');
          }
        } else {
          if (w <= 767) {
            this.grid.set('50%');
          } else if (w <= 1000) {
            this.grid.set('33.3333%');
          } else {
            this.grid.set('25%');
          }
        }
      });
  }

  afterClickItem(item: carouselDatas) {
    if (this.buildFor === 'FrontPage') {
      this.dataCollectionService.addPoint({ eventId: 30022, actionValue1: item.id });
    }
  }
}
